import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { apiFetch, apiFetchBlob } from '../api/client.js';
import { APP_LOCALE } from '../utils/locale.js';

type Kind = 'incoming' | 'withdrawal' | 'payment';
interface ReviewDocument { id: string; filename: string; contentType: string; sizeBytes: number;
  createdAt: string; source: 'CUSTOMER' | 'STAFF'; uploadedBy: string | null }
interface Upload { key: string; file: File; state: 'waiting' | 'uploading' | 'failed'; error?: string }
const MAX_BYTES = 10 * 1024 * 1024;
const PdfEvidencePreview = lazy(() => import('./PdfEvidencePreview.js'));
const bytes = (size: number): string => size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(size / 1024))} KB`;
function save(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a'); link.href = url; link.download = filename; link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** One evidence workflow for incoming credits, outgoing payment orders and withdrawals. */
export function ReviewDocumentsPanel({ kind, id, canWrite }: { kind: Kind; id: string; canWrite: boolean }): JSX.Element {
  const base = `/admin/review-documents/${kind}/${id}`;
  const client = useQueryClient();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const input = useRef<HTMLInputElement>(null);
  const active = useRef(true);
  const uploading = useRef(false);
  const previewRequest = useRef(0);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dragging, setDragging] = useState(false);
  const [opening, setOpening] = useState(false);
  const [preview, setPreview] = useState<{ doc: ReviewDocument; blob: Blob | null; error: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const docs = useQuery({ queryKey: ['review-documents', kind, id], queryFn: () => apiFetch<ReviewDocument[]>(base) });
  useEffect(() => { active.current = true; return () => { active.current = false; previewRequest.current++; }; }, []);
  useEffect(() => {
    if (!preview?.blob) { setPreviewUrl(''); return; }
    const url = URL.createObjectURL(preview.blob); setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview?.blob]);

  const upload = async (batch: Upload[]): Promise<void> => {
    if (uploading.current || !canWrite) return;
    uploading.current = true; setError(''); setNotice('');
    let saved = 0;
    for (const item of batch) {
      if (!active.current) break;
      setUploads(current => current.map(row => row.key === item.key ? { ...row, state: 'uploading', error: '' } : row));
      const form = new FormData(); form.append('file', item.file);
      try {
        await apiFetch(base, { method: 'POST', body: form, headers: { 'Idempotency-Key': item.key } });
        saved++;
        if (active.current) setUploads(current => current.filter(row => row.key !== item.key));
      } catch (failure) {
        if (active.current) setUploads(current => current.map(row => row.key === item.key ? { ...row, state: 'failed', error: failure instanceof Error ? failure.message : 'Upload failed. Try again.' } : row));
      }
    }
    uploading.current = false;
    await client.invalidateQueries({ queryKey: ['review-documents', kind, id] });
    if (active.current && saved) setNotice(`${saved} ${saved === 1 ? 'file added' : 'files added'}.`);
  };
  const choose = (files: File[]): void => {
    if (!canWrite || uploading.current) return;
    setError('');
    if (files.length > 10) { setError('Choose up to 10 files at a time.'); return; }
    if (files.some(file => file.size === 0 || file.size > MAX_BYTES)) { setError('Choose non-empty files of 10 MB or smaller.'); return; }
    const batch: Upload[] = files.map(file => ({ key: crypto.randomUUID(), file, state: 'waiting' }));
    setUploads(current => [...current, ...batch]); void upload(batch);
  };
  const openPreview = async (doc: ReviewDocument): Promise<void> => {
    const request = ++previewRequest.current;
    setPreview({ doc, blob: null, error: '' });
    try {
      const blob = await apiFetchBlob(`${base}/${doc.id}`);
      if (active.current && request === previewRequest.current) setPreview({ doc, blob, error: '' });
    } catch (failure) {
      if (active.current && request === previewRequest.current) setPreview({ doc, blob: null, error: failure instanceof Error ? failure.message : 'Preview could not be loaded.' });
    }
  };
  const download = async (doc: ReviewDocument): Promise<void> => {
    setOpening(true); setError('');
    try { save(await apiFetchBlob(`${base}/${doc.id}`), doc.filename); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'Download failed. Try again.'); }
    finally { if (active.current) setOpening(false); }
  };
  const closePreview = (): void => { previewRequest.current++; setPreview(null); };
  const busy = uploads.some(row => row.state === 'uploading');

  return <Paper variant="outlined" component="section" aria-label="Supporting documents" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" fontWeight={700}>Supporting documents{docs.data ? ` (${docs.data.length})` : ''}</Typography>
      {canWrite && <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon />} onClick={() => input.current?.click()} disabled={busy}>Add files</Button>}
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Invoices, contracts and other evidence for this transaction.</Typography>
    {canWrite && <Box onDragOver={event => { event.preventDefault(); if (!busy) setDragging(true); }} onDragLeave={() => setDragging(false)}
      onDrop={event => { event.preventDefault(); setDragging(false); choose(Array.from(event.dataTransfer.files)); }}
      sx={{ border: '1px dashed', borderColor: dragging ? 'primary.main' : 'divider', borderRadius: 2, p: 2, mb: 2, bgcolor: dragging ? 'action.hover' : 'background.default' }}>
      <input ref={input} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" aria-label="Choose supporting documents" style={{ display: 'none' }}
        onChange={event => { choose(Array.from(event.target.files ?? [])); event.target.value = ''; }} />
      <Typography variant="body2">Drop files here or use Add files</Typography>
      <Typography variant="caption" color="text.secondary">PDF, JPG, PNG, WebP or HEIC · 10 MB per file</Typography>
    </Box>}
    {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
    {notice && <Typography role="status" variant="body2" color="success.main" sx={{ mb: 1.5 }}>{notice}</Typography>}
    {uploads.map(item => <Box key={item.key} sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{item.file.name}</Typography>
      {item.state === 'uploading' ? <><LinearProgress aria-label={`Uploading ${item.file.name}`} sx={{ my: 1 }} /><Typography variant="caption" role="status">Uploading…</Typography></> : null}
      {item.state === 'failed' && <Alert severity="error" action={<Button disabled={busy} onClick={() => void upload([item])}>Retry</Button>}>{item.error}</Alert>}
    </Box>)}
    {docs.isPending ? <CircularProgress size={22} aria-label="Loading supporting documents" /> : docs.isError ? <Alert severity="error" action={<Button onClick={() => void docs.refetch()}>Retry</Button>}>Supporting documents could not be loaded.</Alert>
      : !docs.data.length ? <Typography variant="body2" color="text.secondary">No supporting documents attached yet.</Typography>
      : <Stack spacing={1.5}>{docs.data.map(doc => <Box key={doc.id} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5, minWidth: 0 }}>
        <Stack direction="row" gap={1} alignItems="flex-start"><InsertDriveFileOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} /><Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{doc.filename}</Typography>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}><Chip size="small" variant="outlined" label={doc.source === 'STAFF' ? 'Staff' : 'Customer'} />
            <Typography variant="caption" color="text.secondary">{bytes(doc.sizeBytes)} · {new Date(doc.createdAt).toLocaleString(APP_LOCALE)}</Typography></Stack>
        </Box></Stack>
        <Stack direction="row" gap={1} sx={{ mt: 1 }}><Button size="small" onClick={() => void openPreview(doc)}>Preview</Button><Button size="small" disabled={opening} onClick={() => void download(doc)}>Download</Button></Stack>
      </Box>)}</Stack>}
    <Dialog open={preview !== null} onClose={closePreview} fullWidth maxWidth="lg" fullScreen={mobile}>
      <DialogTitle sx={{ overflowWrap: 'anywhere', pr: 3 }}>{preview?.doc.filename}</DialogTitle>
      <DialogContent dividers sx={{ p: 1, height: { xs: '100%', sm: '70vh' }, bgcolor: 'background.default', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {preview?.error ? <Alert severity="error" action={<Button onClick={() => void openPreview(preview.doc)}>Retry</Button>}>{preview.error}</Alert>
          : !preview?.blob || !previewUrl ? <CircularProgress aria-label="Loading document preview" />
          : preview.blob.type === 'application/pdf' ? <Suspense fallback={<CircularProgress aria-label="Loading PDF viewer" />}><PdfEvidencePreview blob={preview.blob} name={preview.doc.filename} /></Suspense>
          : ['image/png', 'image/jpeg', 'image/webp'].includes(preview.blob.type) ? <Box component="img" src={previewUrl} alt={preview.doc.filename} sx={{ display: 'block', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : <Alert severity="info">This file cannot be previewed in your browser. Download it to view it.</Alert>}
      </DialogContent>
      <DialogActions><Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1, display: { xs: 'none', sm: 'block' } }}>If the preview is unavailable, download the file.</Typography>
        <Button disabled={!preview?.blob} onClick={() => { if (preview?.blob) save(preview.blob, preview.doc.filename); }}>Download</Button><Button variant="outlined" sx={{ whiteSpace: 'nowrap' }} onClick={closePreview}>Close preview</Button></DialogActions>
    </Dialog>
  </Paper>;
}
